<template>
  <div v-if="shouldShowPrompt" class="push-notification-prompt">
    <b-notification
      type="is-info"
      has-icon
      icon="bell"
      :closable="true"
      @close="handleDismiss"
      aria-close-label="Close notification"
    >
      <div class="notification-content">
        <p class="has-text-weight-semibold mb-2">
          {{ isIOS && !isInstalled ? 'Instalează aplicația pentru notificări' : 'Activează notificările push' }}
        </p>
        <p class="mb-3">
          <template v-if="isIOS && !isInstalled">
            Pentru a primi notificări despre documente care expiră, adaugă aplicația pe ecranul principal.
            Apasă pe <b-icon icon="export-variant" size="is-small"></b-icon> și apoi "Add to Home Screen".
          </template>
          <template v-else>
            Primești notificări instant despre documente care expiră.
          </template>
        </p>
        <div class="buttons">
          <b-button
            v-if="!isIOS || isInstalled"
            type="is-primary"
            size="is-small"
            @click="handleActivate"
            :loading="isActivating"
          >
            Activează notificări
          </b-button>
          <b-button
            type="is-text"
            size="is-small"
            @click="handleMaybeLater"
          >
            Mai târziu
          </b-button>
        </div>
      </div>
    </b-notification>
  </div>
</template>

<script>
import pushNotificationService from '@/services/pushNotificationService';

export default {
  name: 'PushNotificationPrompt',
  data() {
    return {
      shouldShowPrompt: false,
      isActivating: false,
      isIOS: false,
      isInstalled: false
    };
  },
  async mounted() {
    console.log('[PushPrompt] Component mounted, checking if should show...');
    await this.checkIfShouldShowPrompt();
  },
  methods: {
    async checkIfShouldShowPrompt() {
      try {
        // Check if push is supported
        if (!pushNotificationService.isPushSupported()) {
          console.log('[PushPrompt] Push not supported, hiding prompt');
          return;
        }

        // Check current permission state
        const permissionState = pushNotificationService.getPermissionState();
        console.log('[PushPrompt] Permission state:', permissionState);

        // If permission is denied, don't show prompt
        if (permissionState === 'denied') {
          console.log('[PushPrompt] Permission denied, hiding prompt');
          return;
        }

        // If permission is already granted, check if subscribed
        if (permissionState === 'granted') {
          console.log('[PushPrompt] Permission granted, checking subscription...');
          try {
            // Increase timeout to 5s to avoid false positives on slow loading
            const isSubscribed = await Promise.race([
              pushNotificationService.isSubscribed(),
              new Promise((_, reject) => setTimeout(() => reject(new Error('Check subscription timeout')), 5000))
            ]);
            console.log('[PushPrompt] Is subscribed:', isSubscribed);
            
            if (isSubscribed) {
              console.log('[PushPrompt] Already subscribed, hiding prompt');
              this.shouldShowPrompt = false;
              return;
            }
          } catch (err) {
            // If we cannot verify subscription (e.g. SW not ready), do NOT show prompt to avoid annoyance
            console.warn('[PushPrompt] Could not verify subscription (timeout/error). Hiding prompt to be safe.', err);
            this.shouldShowPrompt = false;
            return;
          }
        }

        // Check if user dismissed recently (only if permission is not default)
        if (permissionState !== 'default' && pushNotificationService.wasPromptDismissedRecently()) {
          console.log('[PushPrompt] Prompt was dismissed recently, hiding');
          return;
        }

        // Check platform
        this.isIOS = pushNotificationService.isIOS();
        this.isInstalled = pushNotificationService.isInstalled();
        console.log('[PushPrompt] iOS:', this.isIOS, 'Installed:', this.isInstalled);

        // On iOS, only show if installed as PWA
        if (this.isIOS && !this.isInstalled) {
          console.log('[PushPrompt] iOS not installed, showing install prompt in 2s');
          setTimeout(() => {
            this.shouldShowPrompt = true;
          }, 2000);
          return;
        }

        // Show prompt immediately for subscriptions
        console.log('[PushPrompt] Showing subscription prompt in 1s');
        setTimeout(() => {
          console.log('[PushPrompt] NOW showing prompt');
          this.shouldShowPrompt = true;
        }, 1000); // 1 second - quick but not instant
      } catch (error) {
        console.error('[PushPrompt] Error in checkIfShouldShowPrompt:', error);
        // Show prompt anyway on error
        setTimeout(() => {
          console.log('[PushPrompt] Showing prompt after error');
          this.shouldShowPrompt = true;
        }, 1000);
      }
    },
    async handleActivate() {
      console.log('[PushPrompt] User clicked Activate button');
      this.isActivating = true;
      try {
        console.log('[PushPrompt] Calling subscribeToPush...');
        await pushNotificationService.subscribeToPush();
        console.log('[PushPrompt] subscribeToPush completed successfully');
        
        this.$buefy.toast.open({
          message: 'Notificări activate cu succes!',
          type: 'is-success',
          duration: 3000
        });

        this.shouldShowPrompt = false;
        this.$emit('subscribed');
      } catch (error) {
        console.error('[PushPrompt] Failed to activate push notifications:', error);
        
        let errorMessage = 'Nu am putut activa notificările.';
        if (error.message.includes('denied')) {
          errorMessage = 'Ai blocat notificările. Activează-le din setările browserului.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Timeout - verifică dacă service worker-ul este activ.';
        } else if (error.message.includes('VAPID')) {
          errorMessage = 'Nu am putut contacta serverul. Verifică conexiunea.';
        }

        this.$buefy.toast.open({
          message: errorMessage + ' (Detalii în console)',
          type: 'is-danger',
          duration: 5000
        });
      } finally {
        this.isActivating = false;
        console.log('[PushPrompt] handleActivate finished, loading=false');
      }
    },
    handleMaybeLater() {
      pushNotificationService.markPromptAsDismissed();
      this.shouldShowPrompt = false;
      this.$emit('dismissed');
    },
    handleDismiss() {
      this.handleMaybeLater();
    }
  }
};
</script>

<style scoped>
.push-notification-prompt {
  position: fixed;
  bottom: 20px;
  right: 20px;
  left: 20px;
  z-index: 1000;
  max-width: 400px;
  margin-left: auto;
}

@media (max-width: 768px) {
  .push-notification-prompt {
    bottom: 10px;
    right: 10px;
    left: 10px;
  }
}

.notification-content {
  font-size: 0.95rem;
}

.buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
</style>
